<template>
  <div class="topic-article">
    <div class="article-time">
      <div class="time-box">
        {{ timelineText }}
      </div>
    </div>
    <div class="line-wrap">
      <div class="line-circle"></div>
      <div class="line"></div>
    </div>
    <div class="article-wrap">
      <div class="article-box" @click.self="toArticleDetail">
        <div class="article-text" @click="toArticleDetail">
          <span>{{ article.description || article.title }}</span>
          <img
            v-if="article.isShowTimeLink"
            class="article-detail-arrow-img"
            src="@/assets/images/to-detail-arrow.png"
            alt="跳转详情"
          />
        </div>
        <div
          v-if="!videoSrc && article.mListpattern === 10 && imageList.length"
          ref="articleImageBoxRef"
          class="article-img"
          :class="gridClass"
        >
          <img
            v-for="(item, index) in imageList"
            :key="item.id"
            :src="defaultImage"
            :data-src="item.src"
            class="img-child lazy"
            @click="previewImage(index)"
          />
        </div>
        <video
          v-if="videoSrc"
          ref="videoRef"
          webkit-playsinline="true"
          playsinline="true"
          x5-video-player-type="h5"
          :src="videoSrc"
          class="article-video video-js"
          :poster="imageList[0]?.src || defaultImage"
        ></video>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LazyLoad from 'vanilla-lazyload';
import 'viewerjs/dist/viewer.css';
import Viewer from 'viewerjs';
import wx from 'weixin-js-sdk';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';
import { browerVersion, uuid } from '../utils';
import 'video.js/dist/video-js.min.css';

defineOptions({
  name: 'TopicArticle',
});
const defaultImage = import.meta.env.VITE_DEFAULT_IMAGE_16_9;
const version = browerVersion();
const props = defineProps<{ article: Topic.Article }>();
const articleImageBoxRef = ref();
const gridClass = computed(() => {
  if (props.article.mCoverImgList.length === 1) {
    return '';
  }
  if (
    props.article.mCoverImgList.length === 2 ||
    props.article.mCoverImgList.length === 4
  ) {
    return 'grid-cols-2';
  }
  return 'grid-cols-3';
});

const imageList = computed(() => {
  if (props.article.mCoverImgList && props.article.mCoverImgList.length) {
    return props.article.mCoverImgList.map((mCoverImg) => {
      return {
        id: uuid(),
        src: mCoverImg,
      };
    });
  }
  return [];
});

const timelineText = computed(() => {
  if (props.article.timeAxisStr) {
    return props.article.timeAxisStr;
  }
  // if (props.article.shorttitle) {
  //   return props.article.shorttitle;
  // }
  return (props.article.publishTime || props.article.createtime).substring(
    0,
    16,
  );
});

const videoSrc = computed(() => {
  if (
    props.article.contentType === 4 &&
    (props.article.moVideoOsspath || props.article.moVideoPath)
  ) {
    return props.article.moVideoPath || props.article.moVideoOsspath;
  }
  return '';
});

const videoRef = ref();
let videoPlayer: Player;
watch(
  () => videoRef.value,
  () => {
    if (!videoPlayer) {
      videoPlayer = videojs(videoRef.value, {
        controls: true,
        aspectRatio: '16:9',
      });
      videoPlayer.on('play', () => {
        const players = videojs.getAllPlayers();
        players.forEach((player) => {
          if (!player.paused() && player.id() !== videoPlayer.id()) {
            player.pause();
          }
        });
      });
    }
  },
);

const previewImage = (index: number) => {
  const src = props.article.mCoverImgList[index];
  wx.previewImage({
    current: src,
    urls: props.article.mCoverImgList,
  });
};

const cursor = computed(() =>
  props.article.isShowTimeLink ? 'pointer' : 'auto',
);
const toArticleDetail = () => {
  if (props.article.isShowTimeLink) {
    window.open(props.article.url);
  }
};

onMounted(() => {
  // eslint-disable-next-line no-new
  new LazyLoad();
  if (!version.weixin && articleImageBoxRef.value) {
    // eslint-disable-next-line no-new
    new Viewer(articleImageBoxRef.value, {
      url: (image: HTMLImageElement) => {
        return image.getAttribute('data-src');
      },
      toolbar: false,
      navbar: false,
    });
  }
});
</script>
<style lang="scss" scoped>
.topic-article {
  display: flex;
  gap: 4px;
  align-items: stretch;
  width: 100%;
  padding: 0 15px;
  cursor: v-bind('cursor');
  user-select: none;

  .article-time {
    display: flex;
    flex-direction: column;
    width: 20%;
    min-width: 80px;
    text-align: justify;

    .time-box {
      padding: 6px 8px;
      font-size: 0.75rem;
      line-height: 1.5;
      color: var(--timeline-color);
      text-align: center;
      background-color: var(--timeline-background);
      border-radius: 5px;
    }
  }

  .line-wrap {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    width: 19px;

    .line-circle {
      position: absolute;
      top: 0;
      width: 19px;
      height: 19px;
      background: var(--timeline-border-circle);
      background-size: 100% 100%;
    }

    .line {
      width: 1px;
      height: calc(100% - 2px);
      background: var(--timeline-border-line);
    }
  }

  .article-wrap {
    width: 100%;
    padding-bottom: 20px;

    .article-box {
      padding: 10px 12px;
      color: var(--article-color);
      text-align: justify;
      background-color: var(--article-background);
      border-radius: 5px;

      .article-text {
        font-size: 1rem;
        line-height: 1.65;
        color: var(--article-text-color);
        white-space: pre-wrap;

        .article-tip {
          cursor: pointer;
        }

        .article-detail-arrow-img {
          display: inline-block;
          width: 12px;
          margin-left: 10px;
          vertical-align: middle;
        }
      }

      .article-img {
        display: grid;
        gap: 10px;
        width: 100%;
        height: auto;
        margin-top: 10px;

        .img-child {
          width: 100%;
          height: auto;
          object-fit: cover;
          border-radius: 5px;
        }
      }

      .article-video {
        margin-top: 10px;
      }

      .grid-cols-2 {
        grid-template-columns: repeat(2, 1fr);
      }

      .grid-cols-3 {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  }
}
</style>
